// src/Pages/Company/CompanyPublicProfile.jsx

import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Building2,
  Download,
  FileText,
  Globe,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  Truck,
} from "lucide-react";

const getApiBaseUrl = () => {
  const configured = (process.env.REACT_APP_API_PREFIX || "").replace(/\/+$/, "");
  return configured || "https://new.cajuu.app/api";
};

const getStorageBaseUrl = () => {
  const apiBase = getApiBaseUrl();
  return apiBase.replace(/\/api\/?$/, "").replace(/\/+$/, "");
};

const getAssetUrl = (pathOrUrl) => {
  if (!pathOrUrl) return "";
  const value = String(pathOrUrl).trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  return `${getStorageBaseUrl()}/storage/${value.replace(/^\/+/, "")}`;
};

function PublicLayout({ children }) {
  return (
    <div className="min-h-screen bg-[#12110f] text-white font-sans">
      <main>{children}</main>
    </div>
  );
}

function LoaderIcon() {
  return (
    <svg
      className="animate-spin h-12 w-12 text-amber-500"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );
}

function InfoCard({ icon: Icon, label, value, href }) {
  if (!value) return null;

  const content = href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-zinc-100 hover:text-amber-400 transition-colors break-all"
    >
      {value}
    </a>
  ) : (
    <span className="text-zinc-100 break-all">{value}</span>
  );

  return (
    <div className="bg-[#1c1b19] border border-zinc-700 rounded-2xl p-5 hover:border-amber-500/60 transition-colors">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
          <Icon size={21} className="text-amber-500" />
        </div>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.18em] text-zinc-500 font-semibold">
            {label}
          </p>
          <div className="text-sm sm:text-base mt-2">{content}</div>
        </div>
      </div>
    </div>
  );
}

export default function CompanyPublicProfile() {
  const { id, slug } = useParams();
  const profileParam = slug || id;

  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const api = useMemo(() => axios.create({ baseURL: getApiBaseUrl() }), []);

  useEffect(() => {
    if (!profileParam) {
      setError("Company identifier is missing from the URL.");
      setLoading(false);
      return;
    }

    const fetchCompanyData = async () => {
      try {
        const response = await api.get(`/partnercompany/public/${profileParam}`);

        if (response.data?.data) {
          setCompanyData(response.data.data);
        } else {
          throw new Error("Company data not found in API response.");
        }
      } catch (err) {
        console.error("Failed to fetch company data:", err);
        setError("Could not retrieve company details. Please check the link and try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [api, profileParam]);

  const logoUrl = getAssetUrl(companyData?.logo_url || companyData?.logo_path);
  const insuranceDocumentUrl = getAssetUrl(
    companyData?.insurance_document_url || companyData?.insurance_document_path
  );

  const address = [
    companyData?.address,
    companyData?.city,
    companyData?.state,
    companyData?.zipcode,
  ]
    .filter(Boolean)
    .join(", ");

  const websiteValue = companyData?.website_path || companyData?.website || "";
  const websiteHref = websiteValue
    ? websiteValue.startsWith("http")
      ? websiteValue
      : `https://${websiteValue}`
    : "";

  if (loading) {
    return (
      <PublicLayout>
        <div className="flex justify-center items-center min-h-screen">
          <LoaderIcon />
        </div>
      </PublicLayout>
    );
  }

  if (error) {
    return (
      <PublicLayout>
        <div className="flex justify-center items-center min-h-screen text-red-400 text-lg p-4 text-center">
          <p>{error}</p>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <section className="relative overflow-hidden border-b border-zinc-800">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-black/40"></div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="bg-[#1c1b19]/90 border border-zinc-700 rounded-3xl p-6 sm:p-8 shadow-2xl">
            <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-8">
              <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-3xl bg-[#292826] border border-zinc-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={`${companyData.partner_company_name} logo`}
                    className="w-full h-full object-contain p-3"
                  />
                ) : (
                  <Building2 size={52} className="text-zinc-500" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-semibold uppercase tracking-[0.16em] mb-4">
                  <Truck size={14} />
                  Company Profile
                </div>

                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight break-words">
                  {companyData.partner_company_name}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mt-4 text-sm text-zinc-300">
                  {companyData.dot ? (
                    <span className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-[#292826] border border-zinc-700">
                      <ShieldCheck size={16} className="text-amber-500" />
                      DOT {companyData.dot}
                    </span>
                  ) : null}

                  <span className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-[#292826] border border-zinc-700">
                    <ShieldCheck size={16} className="text-amber-500" />
                    {String(companyData.is_allowed_to_operate) === "1"
                      ? "Allowed to operate"
                      : "Company information available"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <InfoCard
            icon={Mail}
            label="Email"
            value={companyData.email}
            href={companyData.email ? `mailto:${companyData.email}` : ""}
          />

          <InfoCard
            icon={Phone}
            label="Phone"
            value={companyData.phone}
            href={companyData.phone ? `tel:${companyData.phone}` : ""}
          />

          <InfoCard icon={MapPin} label="Address" value={address} />

          <InfoCard
            icon={Globe}
            label="Website"
            value={websiteValue}
            href={websiteHref}
          />
        </div>

        <div className="mt-8 bg-[#1c1b19] border border-zinc-700 rounded-3xl p-6 sm:p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                <FileText size={24} className="text-amber-500" />
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-white">
                  Insurance Document
                </h2>
                <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
                  Customers can download the company insurance PDF when it is available.
                </p>
              </div>
            </div>

            {insuranceDocumentUrl ? (
              <a
                href={insuranceDocumentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold transition-colors"
              >
                <Download size={18} />
                Download Insurance PDF
              </a>
            ) : (
              <span className="inline-flex items-center justify-center px-5 py-3 rounded-xl border border-zinc-700 text-zinc-400 text-sm">
                No document available
              </span>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
